// TodosView — calmer-than-default list. Add bar + filter chips +
// grouped rows derived from dueAt buckets. ADHD-friendly framing:
// overdue items in amber under "Earlier" (not a red OVERDUE banner),
// no completion percentage, opt-in importance via a star.
//
// v47 — group-by-tag toggle added alongside the date grouping. The
// tag set is user-managed via the Manage Tags sheet (small modal off
// this page). The grouping mode persists in localStorage so the
// chosen view is sticky across reloads.

import { useEffect, useMemo, useState } from "preact/hooks";
import "../../styles/todos.css";

import { Icon } from "../../icons";
import { useTodos } from "../../lib/use-todos";
import { useTodoTags } from "../../lib/use-todo-tags";
import { confirm } from "../../components/confirm";
import type { TodoItem } from "../../lib/rpc";
import { TodoRow } from "./todo-row";
import { ManageTagsSheet } from "./manage-tags";
import { GROUP_ORDER, NO_TAG_BUCKET, bucketForDueAt, type Bucket } from "./helpers";

type Filter = "open" | "done" | "all";
type Grouping = "date" | "tag";

const GROUPING_LS_KEY = "todos.grouping";

function loadGrouping(): Grouping {
  if (typeof window === "undefined") return "date";
  const v = window.localStorage.getItem(GROUPING_LS_KEY);
  return v === "tag" ? "tag" : "date";
}

interface TodosViewProps {
  notify: (msg: string) => void;
}

export function TodosView({ notify }: TodosViewProps) {
  const t = useTodos();
  const tagsHook = useTodoTags();
  const [draft, setDraft] = useState("");
  const [filter, setFilter] = useState<Filter>("open");
  const [grouping, setGrouping] = useState<Grouping>(loadGrouping);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [manageOpen, setManageOpen] = useState(false);

  // Bubble errors up as toasts so the user notices a stuck network call.
  useEffect(() => { if (t.error) notify(t.error); }, [t.error, notify]);
  useEffect(() => { if (tagsHook.error) notify(tagsHook.error); }, [tagsHook.error, notify]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(GROUPING_LS_KEY, grouping);
  }, [grouping]);

  const open = t.items.filter((i) => !i.done);
  const done = t.items.filter((i) => i.done);
  const visible: TodoItem[] = filter === "open" ? open : filter === "done" ? done : t.items;

  // Date grouping (the historical default) — bucket by due bucket,
  // with done items going to their own bucket regardless of date.
  const groupedByDate = useMemo<Record<string, TodoItem[]>>(() => {
    const m: Record<string, TodoItem[]> = {};
    for (const item of visible) {
      const key: Bucket = item.done ? "Done" : bucketForDueAt(item.dueAt);
      (m[key] ||= []).push(item);
    }
    return m;
  }, [visible]);

  // Tag grouping — bucket by the item's primary tag, falling back to
  // the NO_TAG_BUCKET sentinel. Done items still get their own group
  // at the bottom for consistency with the date view.
  const groupedByTag = useMemo<Record<string, TodoItem[]>>(() => {
    const m: Record<string, TodoItem[]> = {};
    for (const item of visible) {
      if (item.done) { (m["Done"] ||= []).push(item); continue; }
      const tag = (item.tags && item.tags[0]) || NO_TAG_BUCKET;
      (m[tag] ||= []).push(item);
    }
    return m;
  }, [visible]);

  const grouped = grouping === "date" ? groupedByDate : groupedByTag;

  // Order keys: date mode uses GROUP_ORDER; tag mode uses the user's
  // sort_order from the managed tag list, with unknown tags last and
  // "Done" at the very end.
  const orderedKeys = useMemo<string[]>(() => {
    if (grouping === "date") {
      return Object.keys(grouped).sort((a, b) => {
        const ai = GROUP_ORDER.indexOf(a as Bucket);
        const bi = GROUP_ORDER.indexOf(b as Bucket);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });
    }
    const orderIndex = new Map<string, number>();
    tagsHook.tags.forEach((t, i) => orderIndex.set(t.name, i));
    const present = Object.keys(grouped).filter((k) => k !== "Done" && k !== NO_TAG_BUCKET);
    present.sort((a, b) => {
      const ai = orderIndex.has(a) ? (orderIndex.get(a) as number) : 999;
      const bi = orderIndex.has(b) ? (orderIndex.get(b) as number) : 999;
      if (ai !== bi) return ai - bi;
      return a.localeCompare(b);
    });
    const result = present;
    if (grouped[NO_TAG_BUCKET]?.length) result.push(NO_TAG_BUCKET);
    if (grouped["Done"]?.length) result.push("Done");
    return result;
  }, [grouped, grouping, tagsHook.tags]);

  const onAdd = async () => {
    const text = draft.trim();
    if (!text) return;
    setDraft("");
    await t.add(text);
    notify("Added");
  };

  return (
    <div className="todos-page">
      <header className="todos-head">
        <div className="eyebrow">To-do</div>
        <h1 className="h-display">
          A few things,{" "}
          <span className="h-italic" style={{ color: "var(--accent)" }}>when you're ready.</span>
        </h1>
      </header>

      <div className="todo-add">
        <span className="todo-cbx" aria-hidden />
        <input
          placeholder="Add a thing… (tap to set a due date and notes after)"
          value={draft}
          onInput={(e) => setDraft((e.currentTarget as HTMLInputElement).value)}
          onKeyDown={(e) => { if (e.key === "Enter") onAdd(); }}
          disabled={t.loading || !t.list}
        />
        <span className="kbd">⏎</span>
      </div>

      <div className="todos-filters">
        {(["open", "done", "all"] as const).map((f) => (
          <button
            key={f}
            className="chip"
            data-active={filter === f}
            onClick={() => setFilter(f)}
            type="button"
          >
            {f === "open" ? `To do · ${open.length}`
              : f === "done" ? `Done · ${done.length}`
              : `All · ${t.items.length}`}
          </button>
        ))}
        <div className="todos-groupby">
          <span className="todos-groupby-label">Group</span>
          {(["date", "tag"] as const).map((g) => (
            <button
              key={g}
              className="chip"
              data-active={grouping === g}
              onClick={() => setGrouping(g)}
              type="button"
            >
              {g === "date" ? "Date" : "Tag"}
            </button>
          ))}
        </div>
        <button className="todos-manage-tags" type="button" onClick={() => setManageOpen(true)}>
          <Icon name="edit" size={11} />
          Manage tags
        </button>
        {done.length > 0 && (
          <button
            className="todos-clear-done"
            onClick={async () => {
              const ok = await confirm({
                title: "Clear done?",
                message: `${done.length} completed item${done.length === 1 ? "" : "s"} will be removed.`,
                confirmLabel: "Clear",
                danger: true,
              });
              if (!ok) return;
              await t.clearDone();
              notify("Cleared");
            }}
            type="button"
          >
            clear done
          </button>
        )}
      </div>

      {t.loading && (
        <p style={{ color: "var(--text-dim)", fontFamily: "var(--f-mono)", fontSize: 12 }}>loading…</p>
      )}

      {!t.loading && (
        <div className="todos-groups">
          {orderedKeys.map((g) => (
            <div key={g}>
              <div className="todos-group-head" data-grouping={grouping}>
                <div className="eyebrow">{groupHeadLabel(g, grouping)}</div>
                <span className="todos-group-rule" />
                <span className="todos-group-count">{grouped[g].length}</span>
              </div>
              <div className="todos-list">
                {grouped[g].map((item) => (
                  <TodoRow
                    key={item.id}
                    todo={item}
                    expanded={expandedId === item.id}
                    onToggle={() => t.toggle(item.id)}
                    onRemove={() => {
                      t.remove(item.id);
                      if (expandedId === item.id) setExpandedId(null);
                    }}
                    onExpand={() => setExpandedId(expandedId === item.id ? null : item.id)}
                    onUpdate={(patch) => t.update(item.id, patch)}
                    tagOptions={tagsHook.tags}
                  />
                ))}
              </div>
            </div>
          ))}
          {visible.length === 0 && (
            <div className="todos-empty">
              <Icon name="check" size={36} />
              <div className="todos-empty-line">Nothing here. Go outside.</div>
            </div>
          )}
        </div>
      )}

      {manageOpen && (
        <ManageTagsSheet
          tags={tagsHook}
          notify={notify}
          onClose={() => setManageOpen(false)}
        />
      )}
    </div>
  );
}

// Group header text. In date mode the key IS the label. In tag mode
// we render #tag (or "Untagged" for the NO_TAG_BUCKET sentinel) so
// the heading reads naturally.
function groupHeadLabel(key: string, grouping: Grouping): string {
  if (grouping === "date") return key;
  if (key === "Done") return "Done";
  if (key === NO_TAG_BUCKET) return "Untagged";
  return `#${key}`;
}
